import { supabase } from '../lib/supabase'
import type {
  MarketingPayment,
  MarketingPaymentGroupInput,
  MarketingPaymentTemplate,
} from '../types/marketingNotas'

type PaymentTemplateOwner =
  | { invoice_id: string; contract_id?: null }
  | { invoice_id?: null; contract_id: string }

export type PaymentTemplateRowInput = {
  invoice_id: string | null
  contract_id: string | null
  payment_method_id: string
  group_index: number
  installment_number: number
  amount: number
  due_date: string | null
  month_offset: number | null
  due_day: number | null
}

export type PaymentRowInput = {
  invoice_id: string | null
  contract_id: string | null
  payment_method_id: string
  group_index: number
  installment_number: number
  amount: number
  due_date: string
  recurrence_competence: string | null
}

function softDeletePayload(actorId?: string | null, reason = 'replaced on update') {
  return {
    deleted_at: new Date().toISOString(),
    deleted_by: actorId ?? null,
    deleted_reason: reason,
  }
}

function roundMoney(value: number): number {
  return Math.round(value * 10000) / 10000
}

function monthStart(date: string): string {
  return `${date.slice(0, 7)}-01`
}

function monthsBetween(from: string, to: string): number {
  const fromYear = Number(from.slice(0, 4))
  const fromMonth = Number(from.slice(5, 7))
  const toYear = Number(to.slice(0, 4))
  const toMonth = Number(to.slice(5, 7))
  return (toYear - fromYear) * 12 + (toMonth - fromMonth)
}

export function detectRecurringCompetence(payments: MarketingPaymentGroupInput[]): string | null {
  const dueDates = payments
    .flatMap((group) => group.installments.map((installment) => installment.due_date))
    .filter(Boolean)
    .sort()
  const firstDueDate = dueDates[0]
  return firstDueDate ? monthStart(firstDueDate) : null
}

export function mapPaymentTemplate(row: MarketingPaymentTemplate): MarketingPaymentTemplate {
  return {
    ...row,
    amount: Number(row.amount),
    group_index: Number(row.group_index),
    installment_number: Number(row.installment_number),
    due_date: row.due_date || null,
    month_offset: row.month_offset == null ? null : Number(row.month_offset),
    due_day: row.due_day == null ? null : Number(row.due_day),
    payment_method: row.payment_method || null,
  }
}

export function snapshotTemplateRows(
  templates: MarketingPaymentTemplate[] | undefined
): PaymentTemplateRowInput[] {
  return (templates || [])
    .filter((template) => !template.deleted_at)
    .map((template) => ({
      invoice_id: template.invoice_id,
      contract_id: template.contract_id,
      payment_method_id: template.payment_method_id,
      group_index: Number(template.group_index),
      installment_number: Number(template.installment_number),
      amount: roundMoney(Number(template.amount)),
      due_date: template.due_date || null,
      month_offset: template.month_offset == null ? null : Number(template.month_offset),
      due_day: template.due_day == null ? null : Number(template.due_day),
    }))
}

export function snapshotPaymentRows(
  payments: MarketingPayment[] | undefined
): PaymentRowInput[] {
  return (payments || [])
    .filter((payment) => !payment.deleted_at)
    .map((payment) => ({
      invoice_id: payment.invoice_id,
      contract_id: payment.contract_id,
      payment_method_id: payment.payment_method_id,
      group_index: Number(payment.group_index),
      installment_number: Number(payment.installment_number),
      amount: roundMoney(Number(payment.amount)),
      due_date: payment.due_date,
      recurrence_competence: payment.recurrence_competence || null,
    }))
}

export function buildPaymentTemplateRows(
  owner: PaymentTemplateOwner,
  payments: MarketingPaymentGroupInput[],
  recurring = false
): {
  recurrenceCompetence: string | null
  rows: PaymentTemplateRowInput[]
} {
  const recurrenceCompetence = recurring ? detectRecurringCompetence(payments) : null

  const rows = payments.flatMap((group, groupIndex) =>
    group.installments.map((installment, installmentIndex) => ({
      invoice_id: owner.invoice_id ?? null,
      contract_id: owner.contract_id ?? null,
      payment_method_id: group.payment_method_id,
      group_index: group.group_index ?? groupIndex,
      installment_number: installment.installment_number || installmentIndex + 1,
      amount: roundMoney(Number(installment.amount)),
      due_date: installment.due_date || null,
      month_offset:
        recurring && recurrenceCompetence
          ? monthsBetween(recurrenceCompetence, monthStart(installment.due_date))
          : null,
      due_day:
        recurring && installment.due_date ? Number(installment.due_date.slice(8, 10)) : null,
    }))
  )

  return {
    recurrenceCompetence,
    rows,
  }
}

export function buildPaymentRowsFromTemplates(
  templates: PaymentTemplateRowInput[],
  recurrenceCompetence: string | null = null
): PaymentRowInput[] {
  return templates.map((template) => {
    if (!template.due_date) {
      throw new Error('Template de pagamento sem vencimento não pode gerar parcela imediata.')
    }
    return {
      invoice_id: template.invoice_id,
      contract_id: template.contract_id,
      payment_method_id: template.payment_method_id,
      group_index: template.group_index,
      installment_number: template.installment_number,
      amount: template.amount,
      due_date: template.due_date,
      recurrence_competence: recurrenceCompetence,
    }
  })
}

export const paymentTemplatesService = {
  async replaceForOwner(
    owner: PaymentTemplateOwner,
    payments: MarketingPaymentGroupInput[],
    options?: {
      recurring?: boolean
      actorId?: string | null
      deleteReason?: string
    }
  ): Promise<{
    recurrenceCompetence: string | null
    rows: PaymentTemplateRowInput[]
  }> {
    const ownerColumn = owner.contract_id ? 'contract_id' : 'invoice_id'
    const ownerId = owner.contract_id || owner.invoice_id
    const replacePayload = softDeletePayload(
      options?.actorId ?? null,
      options?.deleteReason || 'replaced on update'
    )

    const { error: softError } = await supabase
      .from('payment_templates')
      .update(replacePayload)
      .eq(ownerColumn, ownerId)
      .is('deleted_at', null)

    if (softError) throw softError

    const built = buildPaymentTemplateRows(owner, payments, options?.recurring === true)
    if (!built.rows.length) return built

    const { error: insertError } = await supabase.from('payment_templates').insert(built.rows)
    if (insertError) throw insertError

    return built
  },

  async restoreForOwner(
    owner: PaymentTemplateOwner,
    rows: PaymentTemplateRowInput[],
    options?: {
      actorId?: string | null
      deleteReason?: string
    }
  ): Promise<void> {
    const ownerColumn = owner.contract_id ? 'contract_id' : 'invoice_id'
    const ownerId = owner.contract_id || owner.invoice_id
    const replacePayload = softDeletePayload(
      options?.actorId ?? null,
      options?.deleteReason || 'rollback restore templates'
    )

    const { error: softError } = await supabase
      .from('payment_templates')
      .update(replacePayload)
      .eq(ownerColumn, ownerId)
      .is('deleted_at', null)
    if (softError) throw softError

    if (!rows.length) return

    const { error: insertError } = await supabase.from('payment_templates').insert(rows)
    if (insertError) throw insertError
  },
}
